// Reports and Privacy Bee University fixtures.
//
// Dashboard/exposure statistics under people/{id}/exposures/... belong to a different route
// file — nothing here claims that namespace.

import { formatBytes } from '@privacy-bee/format/bytes';
import { formatDate } from '@privacy-bee/format/date';
import type { Report, ReportsResponse } from '~/services/reports-service';
import type {
    CurrentModule,
    Module,
    ModuleInfo,
    PersonVideo,
    UniversityStatsResponse,
    Video,
} from '~/types/person/university';
import type { DemoRoute } from '../types';
import { envelope } from '../envelope';
import { DEMO_PERSON_ID, demoDaysAgo } from '../persona';

function slugify(title: string): string {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

// ---- Privacy Bee University ------------------------------------------------------------------

interface LessonSeed {
    id: number;
    order: number;
    title: string;
    description: string;
    length: number; // seconds
    watched: number; // seconds
    completedDaysAgo: number | null; // null = not yet complete
}

interface ModuleSeed {
    id: string;
    order: number;
    name: string;
    description: string;
    lessons: LessonSeed[];
}

const MODULE_SEEDS: ModuleSeed[] = [
    {
        id: 'core_privacy',
        order: 1,
        name: 'Core Privacy Fundamentals',
        description: 'Learn the essentials of protecting your personal information online.',
        lessons: [
            {
                id: 101,
                order: 1,
                title: 'Why Your Personal Data Has Value',
                description: 'How data brokers, advertisers, and scammers profit from the details you give away for free.',
                length: 280,
                watched: 280,
                completedDaysAgo: 420,
            },
            {
                id: 102,
                order: 2,
                title: 'Understanding Data Brokers',
                description: 'Who is buying and selling your name, address, and habits, and how to find out what they hold.',
                length: 310,
                watched: 310,
                completedDaysAgo: 410,
            },
            {
                id: 103,
                order: 3,
                title: 'Reading Privacy Policies Like a Pro',
                description: 'The handful of clauses worth reading in any privacy policy, and how to skip the rest.',
                length: 340,
                watched: 340,
                completedDaysAgo: 395,
            },
            {
                id: 104,
                order: 4,
                title: 'Locking Down Your Social Media',
                description: 'A walkthrough of the privacy settings that actually matter on the platforms you use every day.',
                length: 260,
                watched: 260,
                completedDaysAgo: 380,
            },
            {
                id: 105,
                order: 5,
                title: 'Building a Privacy-First Mindset',
                description: 'Simple habits that keep your exposure low without turning everyday life into a chore.',
                length: 300,
                watched: 300,
                completedDaysAgo: 365,
            },
        ],
    },
    {
        id: 'cybersecurity',
        order: 2,
        name: 'Cybersecurity Essentials',
        description: 'Practical steps to secure your accounts, devices, and passwords.',
        lessons: [
            {
                id: 201,
                order: 1,
                title: 'Password Hygiene 101',
                description: 'Why reused passwords are the single biggest risk to your accounts, and how a password manager fixes it.',
                length: 360,
                watched: 360,
                completedDaysAgo: 40,
            },
            {
                id: 202,
                order: 2,
                title: 'Setting Up Two-Factor Authentication',
                description: 'Turning on 2FA the right way, and which method to pick when a site offers you a choice.',
                length: 420,
                watched: 420,
                completedDaysAgo: 25,
            },
            {
                id: 203,
                order: 3,
                title: 'Spotting Phishing Attempts',
                description: 'The tells that separate a real message from an attempt to steal your credentials.',
                length: 480,
                watched: 280,
                completedDaysAgo: null,
            },
            {
                id: 204,
                order: 4,
                title: 'Securing Your Home Network',
                description: 'Router settings, guest networks, and firmware updates that keep outsiders off your Wi-Fi.',
                length: 390,
                watched: 0,
                completedDaysAgo: null,
            },
            {
                id: 205,
                order: 5,
                title: 'Safe Browsing Habits',
                description: 'Extensions, DNS settings, and everyday habits that cut down on tracking and malware.',
                length: 300,
                watched: 0,
                completedDaysAgo: null,
            },
        ],
    },
    {
        id: 'legal_compliance',
        order: 3,
        name: 'Legal & Compliance Basics',
        description: 'Understand your rights and the laws that protect your personal data.',
        lessons: [
            {
                id: 301,
                order: 1,
                title: 'Your Rights Under the CCPA',
                description: 'What California residents can demand from any company holding their data, and how to ask.',
                length: 330,
                watched: 0,
                completedDaysAgo: null,
            },
            {
                id: 302,
                order: 2,
                title: 'How GDPR Affects US Consumers',
                description: 'Why a European law still matters if you use services headquartered abroad.',
                length: 360,
                watched: 0,
                completedDaysAgo: null,
            },
            {
                id: 303,
                order: 3,
                title: 'Filing a Data Removal Request',
                description: 'The exact language that gets a data broker to act instead of stalling.',
                length: 300,
                watched: 0,
                completedDaysAgo: null,
            },
            {
                id: 304,
                order: 4,
                title: 'Understanding Breach Notification Laws',
                description: 'What a company is legally required to tell you after a breach, and by when.',
                length: 270,
                watched: 0,
                completedDaysAgo: null,
            },
            {
                id: 305,
                order: 5,
                title: 'When to Escalate to a Regulator',
                description: 'When a polite request is not enough, and which agency to file a complaint with.',
                length: 390,
                watched: 0,
                completedDaysAgo: null,
            },
        ],
    },
];

// The share of a lesson the API has to be told was watched, past which it completes the lesson.
const COMPLETION_SHARE = 0.9;

// The account is mid-way through Cybersecurity Essentials: Core Privacy is finished and Legal &
// Compliance has not been opened, so the progress UI has all three states (done / in progress /
// untouched) to render.
const IN_PROGRESS_MODULE_ID = 'cybersecurity';

function buildPersonVideo(lesson: LessonSeed): PersonVideo {
    return {
        id: lesson.id + 9000,
        person_id: DEMO_PERSON_ID,
        pbu_video_id: lesson.id,
        time_watched: lesson.watched,
        completed_at: lesson.completedDaysAgo !== null ? demoDaysAgo(lesson.completedDaysAgo) : null,
    };
}

function buildVideo(moduleId: string, lessonsTotal: number, lesson: LessonSeed): Video {
    return {
        id: lesson.id,
        pbu_module_id: moduleId,
        order: lesson.order,
        title: lesson.title,
        slug: slugify(lesson.title),
        description: lesson.description,
        video_id: `demo-${moduleId}-${lesson.order}`,
        lessons_total: lessonsTotal,
        length: lesson.length,
        person_video: buildPersonVideo(lesson),
    };
}

interface ModuleStats {
    lessonsComplete: number;
    timeWatched: number;
    percent: number;
    nextLessonSeed: LessonSeed | null;
}

// Shared by every module-shaped response so lessons_complete/percent are computed exactly once —
// Module.lessons_complete is nullable (a still-provisioning module) but ModuleInfo.lessons_complete
// is not, so callers read the plain number off this instead of a built Module's nullable field.
function computeModuleStats(seed: ModuleSeed): ModuleStats {
    const lessonsComplete = seed.lessons.filter((l) => l.completedDaysAgo !== null).length;

    return {
        lessonsComplete,
        timeWatched: seed.lessons.reduce((sum, l) => sum + l.watched, 0),
        percent: Math.round((lessonsComplete / seed.lessons.length) * 100),
        nextLessonSeed: seed.lessons.find((l) => l.completedDaysAgo === null) ?? null,
    };
}

function buildModule(seed: ModuleSeed): Module {
    const stats = computeModuleStats(seed);

    return {
        id: seed.id,
        name: seed.name,
        description: seed.description,
        status: 'active',
        active: true,
        time_watched: stats.timeWatched,
        lessons_complete: stats.lessonsComplete,
        lessons_total: seed.lessons.length,
        percent: stats.percent,
        next_lesson: stats.nextLessonSeed ? buildVideo(seed.id, seed.lessons.length, stats.nextLessonSeed) : null,
    };
}

function buildCurrentModule(seed: ModuleSeed): CurrentModule {
    const base = buildModule(seed);

    return {
        ...base,
        image: null,
        completion_percent: base.percent,
        videos: seed.lessons.map((l) => buildVideo(seed.id, seed.lessons.length, l)),
    };
}

function buildModuleInfo(seed: ModuleSeed): ModuleInfo {
    const stats = computeModuleStats(seed);
    const videos = seed.lessons.map((l) => buildVideo(seed.id, seed.lessons.length, l));
    const nextLesson = stats.nextLessonSeed ? buildVideo(seed.id, seed.lessons.length, stats.nextLessonSeed) : videos[0];

    return {
        id: seed.id,
        name: seed.name,
        description: seed.description,
        order: seed.order,
        video_count: seed.lessons.length,
        image: null,
        lessons_complete: stats.lessonsComplete,
        completion_percent: stats.percent,
        next_lesson: nextLesson,
        videos,
    };
}

function findModuleSeed(moduleId: string): ModuleSeed {
    return MODULE_SEEDS.find((m) => m.id === moduleId) ?? MODULE_SEEDS[0];
}

// Looked up by lesson id alone, as the API looks a lesson up: lesson ids are unique across every
// module. The module in the URL names the module the answer describes.
function findLessonById(lessonId: number): LessonSeed {
    for (const module of MODULE_SEEDS) {
        const lesson = module.lessons.find((l) => l.id === lessonId);
        if (lesson) return lesson;
    }
    return MODULE_SEEDS[0].lessons[0];
}

function buildUniversityStatistics(): UniversityStatsResponse {
    const modules: Record<string, Module> = {};
    for (const seed of MODULE_SEEDS) {
        modules[seed.id] = buildModule(seed);
    }

    const currentModuleSeed = findModuleSeed(IN_PROGRESS_MODULE_ID);
    const currentModule = buildCurrentModule(currentModuleSeed);
    // A demo visitor can finish the module. The API answers a null `next_lesson` (and
    // `current_module`) only once every module is finished; the demo does not model moving on, so
    // it keeps this module current and falls back to its opening lesson, the same way
    // buildModuleInfo does at 100%.
    const nextLessonSeed = computeModuleStats(currentModuleSeed).nextLessonSeed ?? currentModuleSeed.lessons[0];
    const nextLesson = buildVideo(currentModuleSeed.id, currentModuleSeed.lessons.length, nextLessonSeed);

    return {
        current_module: currentModule,
        next_lesson: nextLesson,
        modules,
        activity: [
            { core_privacy: 0, cybersecurity: 1, legal_compliance: 0, timestamp: demoDaysAgo(40) },
            { core_privacy: 0, cybersecurity: 1, legal_compliance: 0, timestamp: demoDaysAgo(25) },
        ],
    };
}

// ---- Reports ------------------------------------------------------------------------------------

interface ReportSeed {
    daysAgo: number;
    sizeBytes: number;
}

// Nine monthly reports spanning back past a year, most recent first once sorted.
const REPORT_SEEDS: ReportSeed[] = [
    { daysAgo: 20, sizeBytes: 1_940_000 },
    { daysAgo: 65, sizeBytes: 1_820_000 },
    { daysAgo: 110, sizeBytes: 2_105_000 },
    { daysAgo: 155, sizeBytes: 1_760_000 },
    { daysAgo: 200, sizeBytes: 1_990_000 },
    { daysAgo: 245, sizeBytes: 1_680_000 },
    { daysAgo: 290, sizeBytes: 1_540_000 },
    { daysAgo: 335, sizeBytes: 1_615_000 },
    { daysAgo: 380, sizeBytes: 1_420_000 },
];

function formatMonthYear(iso: string): string {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

function buildMonthlyReports(): Report[] {
    return REPORT_SEEDS.map((seed, index) => {
        const createdAt = demoDaysAgo(seed.daysAgo);
        const period = formatMonthYear(createdAt);

        return {
            id: `report-monthly-${index + 1}`,
            filename: `Privacy-Bee-Monthly-Report-${period.replace(' ', '-')}.pdf`,
            path: `reports/${DEMO_PERSON_ID}/monthly-${index + 1}.pdf`,
            type: `Monthly Report - ${period}`,
            format: 'pdf',
            status: 'complete',
            size: seed.sizeBytes,
            size_formatted: formatBytes(seed.sizeBytes),
            created_at: createdAt,
            created_at_formatted: formatDate(createdAt),
        };
    });
}

// A generated transparency log is queued, not instant: it lands on the reports list once its
// delay has elapsed, so a demo visitor sees the same brief "generating" state as the real export
// job before the poll in pages/reports.vue picks it up.
const TRANSPARENCY_LOG_TYPE = 'Transparency Log';
const TRANSPARENCY_LOG_DELAY_MS = 6000;
const transparencyLogRequests: number[] = [];

function buildTransparencyLogReport(requestedAt: number): Report {
    const createdAt = new Date(requestedAt).toISOString();
    const sizeBytes = 268_000;

    return {
        id: `report-transparency-log-${requestedAt}`,
        filename: `Privacy-Bee-Transparency-Log-${createdAt.slice(0, 10)}.zip`,
        path: `reports/${DEMO_PERSON_ID}/transparency-log-${requestedAt}.zip`,
        type: TRANSPARENCY_LOG_TYPE,
        format: 'zip',
        status: 'complete',
        size: sizeBytes,
        size_formatted: formatBytes(sizeBytes),
        created_at: createdAt,
        created_at_formatted: formatDate(createdAt),
    };
}

function buildReports(): Report[] {
    const arrivedLogs = transparencyLogRequests
        .filter((requestedAt) => Date.now() - requestedAt >= TRANSPARENCY_LOG_DELAY_MS)
        .map(buildTransparencyLogReport);

    return [...arrivedLogs, ...buildMonthlyReports()];
}

function reportSortValue(report: Report, field: string): string | number {
    switch (field) {
        case 'filename':
            return report.filename;
        case 'type':
            return report.type;
        case 'size':
            return report.size;
        case 'created_at':
        default:
            return report.created_at;
    }
}

function sortReports(reports: Report[], sortBy: string, direction: string): Report[] {
    const sorted = [...reports].sort((a, b) => {
        const left = reportSortValue(a, sortBy);
        const right = reportSortValue(b, sortBy);
        if (left === right) return 0;
        return left < right ? -1 : 1;
    });
    return direction === 'asc' ? sorted : sorted.reverse();
}

// ---- Routes -------------------------------------------------------------------------------------

export const contentRoutes: DemoRoute[] = [
    // Lesson progress: matched before the module-info route below since its pattern is the more
    // specific (longer) path.
    {
        method: 'PATCH',
        pattern: /^people\/\d+\/university\/modules\/(\w+)\/lesson\/(\d+)$/,
        respond: (req) => {
            // Mutates the seed in place — findLessonById hands back the live array element, and the
            // statistics and module reads derive from these same seeds, so the update has to land
            // here for a later read to see it.
            const lesson = findLessonById(Number(req.match[2]));
            const reported = Number(req.body?.watched);
            // The API's rule: a watch time past 90% of the lesson completes it and credits its whole
            // length, the time held only ever rises, and a completion stays. A report that is not a
            // number of seconds, which the API refuses, changes nothing.
            if (Number.isFinite(reported) && reported >= 0) {
                const completes = reported > lesson.length * COMPLETION_SHARE;
                lesson.watched = Math.max(lesson.watched, completes ? lesson.length : Math.trunc(Math.min(reported, lesson.length)));
                if (completes && lesson.completedDaysAgo === null) {
                    lesson.completedDaysAgo = 0;
                }
            }
            // Answered, as the API answers it, with the module the request names.
            return envelope(buildModuleInfo(findModuleSeed(req.match[1])));
        },
    },
    {
        method: 'GET',
        pattern: /^people\/\d+\/university\/modules\/(\w+)$/,
        respond: (req) => envelope(buildModuleInfo(findModuleSeed(req.match[1]))),
    },
    {
        method: 'GET',
        pattern: /^people\/\d+\/university\/statistics$/,
        respond: () => envelope(buildUniversityStatistics()),
    },

    // Reports: the transparency-log and report-id routes sit under literal/narrow segments of
    // /reports, so they are listed ahead of the bare list route.
    {
        method: 'POST',
        pattern: /^people\/\d+\/reports\/transparency-log$/,
        status: 202,
        respond: () => {
            transparencyLogRequests.push(Date.now());
            return { data: null };
        },
    },
    {
        method: 'GET',
        pattern: /^people\/\d+\/reports\/([^/]+)$/,
        respond: (req) => ({
            url: `https://demo-reports.privacybee.com/${DEMO_PERSON_ID}/${req.match[1]}?signature=demo-${req.match[1]}`,
            expires_in: 900,
        }),
    },
    {
        method: 'GET',
        pattern: /^people\/\d+\/reports$/,
        respond: (req): ReportsResponse => {
            const search = req.query.get('search')?.toLowerCase().trim();
            const filtered = search
                ? buildReports().filter(
                      (r) => r.filename.toLowerCase().includes(search) || r.type.toLowerCase().includes(search)
                  )
                : buildReports();

            const sorted = sortReports(filtered, req.query.get('sort_by') || 'created_at', req.query.get('sort_direction') || 'desc');

            const page = Number(req.query.get('page') || '1');
            const perPage = Number(req.query.get('per_page') || '15');
            const start = (page - 1) * perPage;

            return {
                data: sorted.slice(start, start + perPage),
                meta: {
                    current_page: page,
                    per_page: perPage,
                    total: sorted.length,
                    last_page: Math.max(1, Math.ceil(sorted.length / perPage)),
                },
            };
        },
    },
];
